import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './ServicePage.css';

export default function ServiceTemplate({ data }) {
  const { title, subtitle, tagline, heroDesc, icon, features, benefits, process, whyChoose, faq, relatedServices } = data;

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); });
    }, { threshold: 0.1 });
    document.querySelectorAll('.animate-on-scroll').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="service-page">
      {/* HERO */}
      <section className="sp-hero">
        <div className="sp-hero-bg">
          <div className="sp-orb" />
        </div>
        <div className="container sp-hero-content">
          <span className="section-tag">{tagline}</span>
          <div className="sp-hero-icon">{icon}</div>
          <h1>{title}<br /><span>{subtitle}</span></h1>
          <p>{heroDesc}</p>
          <div className="sp-hero-cta">
            <Link to="/contact" className="btn-primary">Get Started Today</Link>
            <a href="tel:+919370115694" className="btn-outline">📞 Call Us</a>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="sp-features">
        <div className="container">
          <div className="section-header animate-on-scroll">
            <span className="section-tag">What's Included</span>
            <h2 className="section-title">Key <span>Features</span></h2>
          </div>
          <div className="sp-features-grid">
            {features.map((f, i) => (
              <div className="spf-card animate-on-scroll" key={i} style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="spf-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="sp-benefits">
        <div className="container">
          <div className="sp-benefits-grid">
            <div className="spb-text animate-on-scroll">
              <span className="section-tag">Business Impact</span>
              <h2 className="section-title">Why You <span>Need This</span></h2>
              <div className="spb-list">
                {benefits.map((b, i) => (
                  <div className="spb-item" key={i}>
                    <span className="spb-check">✓</span>
                    <div>
                      <strong>{b.title}</strong>
                      <p>{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="spb-visual animate-on-scroll">
              <div className="spb-card">
                <div className="spb-stat-big">{data.mainStat}</div>
                <p>{data.mainStatLabel}</p>
                <div className="spb-divider" />
                <div className="spb-sub-stats">
                  {data.subStats && data.subStats.map((s, i) => (
                    <div key={i} className="spb-sub-stat">
                      <span>{s.value}</span>
                      <p>{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="sp-process">
        <div className="container">
          <div className="section-header animate-on-scroll">
            <span className="section-tag">How We Work</span>
            <h2 className="section-title">Our <span>Process</span></h2>
          </div>
          <div className="sp-process-steps">
            {process.map((step, i) => (
              <div className="spp-step animate-on-scroll" key={i} style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="spp-num">{String(i + 1).padStart(2, '0')}</div>
                <div className="spp-line" />
                <div className="spp-content">
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="sp-why">
        <div className="container">
          <div className="section-header animate-on-scroll">
            <span className="section-tag">Why DMS Technologies</span>
            <h2 className="section-title">Why Choose <span>Us?</span></h2>
          </div>
          <div className="sp-why-grid">
            {whyChoose.map((w, i) => (
              <div className="spw-card animate-on-scroll" key={i}>
                <div className="spw-icon">{w.icon}</div>
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="sp-reviews">
        <div className="container">
          <div className="section-header animate-on-scroll">
            <span className="section-tag">Client Testimonials</span>
            <h2 className="section-title">What Clients <span>Say</span></h2>
          </div>
          <div className="sp-reviews-grid">
            {data.reviews.map((r, i) => (
              <div className="spr-card animate-on-scroll" key={i}>
                <div className="spr-stars">{'★★★★★'}</div>
                <p>"{r.text}"</p>
                <div className="spr-author">
                  <div className="spr-avatar">{r.name[0]}</div>
                  <div>
                    <strong>{r.name}</strong>
                    <span>{r.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="sp-faq">
        <div className="container">
          <div className="section-header animate-on-scroll">
            <span className="section-tag">Common Questions</span>
            <h2 className="section-title">FAQ</h2>
          </div>
          <div className="sp-faq-list">
            {faq.map((f, i) => (
              <div className="spfaq-item animate-on-scroll" key={i}>
                <h4>Q: {f.q}</h4>
                <p>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="sp-cta">
        <div className="container">
          <div className="sp-cta-card animate-on-scroll">
            <h2>Ready to Get Started with <span>{title}?</span></h2>
            <p>Contact our team today for a free consultation and custom strategy.</p>
            <div className="sp-cta-btns">
              <Link to="/contact" className="btn-primary">Get Free Consultation</Link>
              <a href="https://wa.me/919370115694" target="_blank" rel="noreferrer" className="btn-outline">WhatsApp Us</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
